/**
 * AVA-531 - client-side PDF export of the AI Analysis job report.
 *
 * Layout: the PM's reference PDF (AVA_High_Accuracy_Structural_Report.pdf), page for page, at its own size -
 * A3 landscape (Khalid, 2026-09-07: "apply the reference as the pdf export"). Design gate:
 * src/assets/showcase/ai-analysis-pdf-directions-showcase.html, direction C.
 *
 *   page 1        masthead; LEFT: Summary cards, the findings table, Inspection basis + Scab / Validation panels;
 *                 RIGHT: the annotated scan, then Validation status / contour limitation
 *   page 2..      table rows that did not fit page 1 (full width), only when there are any
 *   then          "Detailed Findings & Validation": two columns of per-area cards
 *
 * Every string drawn here goes through `asciify`: jsPDF's built-in Helvetica encodes as WinAnsi (cp1252), so
 * an em dash or a degree sign drawn straight from a shared constant silently loses its glyph.
 */
import jsPDF from 'jspdf';
import { error as logError } from '@/utils/debugLogger';
import { APPROVAL, SCAB, computeKpis, relativeArea, parseMaskPoints, stripCat, stripRi } from './findingsMetrics';
import { EM_DASH, formatPercent } from './reportFormat';
import { renderAnnotatedImage } from './annotatedImageCanvas';
import {
  AREA_DECIMALS,
  INSPECTION_BASIS,
  NOT_RECORDED,
  PDF_CONTOUR_LIMITATION,
  PDF_DETAIL_TITLE,
  PDF_NO_INFERENCE,
  REPORT_MODE,
  REPORT_SCOPE,
  SCAB_LEGEND,
} from '../components/aiAnalysis/reportConstants';
import {
  CONTENT_BOTTOM,
  CONTENT_WIDTH,
  COLOR,
  PAGE,
  TYPE,
  box,
  chip,
  lineHeight,
  paragraph,
  paragraphHeight,
  rule,
  setText,
  text,
} from './reportPdfTheme';

const ASCII_MAP = Object.freeze([
  [/[—–]/g, '-'],
  [/[‘’]/g, "'"],
  [/[“”]/g, '"'],
  [/°/g, ' deg'],
  [/²/g, '2'],
  [/≥/g, '>='],
  [/≤/g, '<='],
  [/·/g, '|'],
]);

function asciify(value) {
  return ASCII_MAP.reduce((acc, [pattern, replacement]) => acc.replace(pattern, replacement), String(value == null ? '' : value));
}

/** Confidences are stored 0-1; every confidence the UI shows is x100 (ConfidenceRing.vue:27). */
function percentOf(value) {
  return value == null || value === '' ? '-' : String(Math.round(Number(value) * 100));
}

const SCAB_COLOR = Object.freeze({
  [SCAB.YES]: COLOR.scabYes,
  [SCAB.POSSIBLE]: COLOR.scabPossible,
  [SCAB.NO]: COLOR.scabNo,
});

const REVIEW_LABEL = Object.freeze({
  [APPROVAL.ACCEPTED]: 'Accepted',
  [APPROVAL.REJECTED]: 'Rejected',
});

/* ---------------------------------------------------------------------------------------------------------
   Page 1 geometry. The left column carries the summary and the table, the right column the scan.
   --------------------------------------------------------------------------------------------------------- */
const COLUMN_GAP = 8;
const LEFT_WIDTH = 192;
const RIGHT_X = PAGE.margin + LEFT_WIDTH + COLUMN_GAP;
const RIGHT_WIDTH = CONTENT_WIDTH - LEFT_WIDTH - COLUMN_GAP;
/** The two panels under the table on page 1 (Inspection basis, Scab / Validation). */
const PANEL_HEIGHT = 42;
const PANEL_TOP = CONTENT_BOTTOM - PANEL_HEIGHT;

/* Findings table. Columns are positioned, not padded: Helvetica is proportional. `width: 0` = the remainder. */
const TABLE_COLUMNS = Object.freeze([
  { key: 'id', label: 'ID', width: 9, align: 'center' },
  { key: 'location', label: 'Location / Component', width: 0, align: 'left' },
  { key: 'ri', label: 'Ri', width: 9, align: 'center' },
  { key: 'cat', label: 'Cat', width: 9, align: 'center' },
  { key: 'corr', label: 'Corr.', width: 12, align: 'right' },
  { key: 'poly', label: 'Poly.', width: 12, align: 'right' },
  { key: 'area', label: 'Rel. %', width: 14, align: 'right' },
  { key: 'scab', label: 'Scab', width: 17, align: 'center' },
  { key: 'scabConf', label: 'Scab C.', width: 14, align: 'right' },
  { key: 'review', label: 'Review', width: 16, align: 'center' },
]);
const TABLE_ROW_HEIGHT = 4.6;
const TABLE_HEAD_HEIGHT = 5.6;
const CELL_PAD = 1.4;

function columnGeometry(x0, totalWidth) {
  const fixed = TABLE_COLUMNS.reduce((sum, c) => sum + c.width, 0);
  let x = x0;
  return TABLE_COLUMNS.map((column) => {
    const width = column.width || totalWidth - fixed;
    const geometry = { ...column, x, width };
    x += width;
    return geometry;
  });
}

function cellX(column) {
  if (column.align === 'right') return column.x + column.width - CELL_PAD;
  if (column.align === 'center') return column.x + column.width / 2;
  return column.x + CELL_PAD;
}

function areaPercent(finding, size) {
  return formatPercent(relativeArea(parseMaskPoints(finding.maskPoints), size.width, size.height), AREA_DECIMALS);
}

/** "Component | location" when the finding is linked, so the column keeps its "Location / Component" promise. */
function locationCell(finding) {
  return asciify([finding.masterTagName, finding.locationDescription || EM_DASH].filter(Boolean).join(' | '));
}

function rowFor(finding, size) {
  return {
    id: finding.badgeIndex,
    location: locationCell(finding),
    ri: stripRi(finding.coatingBreakdown) || '-',
    cat: stripCat(finding.pittingCategory) || '-',
    corr: percentOf(finding.detectionConfidence),
    poly: percentOf(finding.polygonConfidence),
    area: areaPercent(finding, size),
    scab: String(finding.scabMeasure || '-').toUpperCase(),
    scabConf: percentOf(finding.scabConfidence),
    review: REVIEW_LABEL[finding.approvalStatus] || 'Pending',
  };
}

/* ---------------------------------------------------------------------------------------------------------
   Furniture
   --------------------------------------------------------------------------------------------------------- */

function drawFooter(doc, model, pageNumber, pageCount) {
  const y = PAGE.height - PAGE.footerGutter + 3;
  rule(doc, PAGE.margin, y - 2, CONTENT_WIDTH);
  text(doc, asciify(`AVA - AI Analysis job report - ${model.jobLabel} - AI proposal, not a validated inspection result.`), PAGE.margin, y, {
    size: TYPE.caption,
    color: COLOR.soft,
  });
  text(doc, `Page ${pageNumber} of ${pageCount}`, PAGE.width - PAGE.margin, y, { size: TYPE.caption, color: COLOR.soft, align: 'right' });
}

function sectionHead(doc, label, x, y) {
  return text(doc, asciify(label).toUpperCase(), x, y, { size: TYPE.sectionHead, style: 'bold', color: COLOR.brand }) + 0.8;
}

/** Masthead: wordmark | title + mode/scope | PROPOSED chip | five meta facts, right-aligned. */
function drawMasthead(doc, model) {
  const y = PAGE.margin;
  setText(doc, { size: TYPE.wordmark, style: 'bold', color: COLOR.brand });
  doc.text('AVA', PAGE.margin, y + 5.5, { baseline: 'middle' });
  const titleX = PAGE.margin + doc.getTextWidth('AVA') + 4;

  text(doc, 'AI INSPECTION REPORT', titleX, y, { size: TYPE.title, style: 'bold', color: COLOR.ink });
  text(doc, asciify(`${REPORT_MODE} | ${REPORT_SCOPE}`), titleX, y + lineHeight(TYPE.title) - 0.5, { size: TYPE.subtitle, color: COLOR.soft });

  if (!model.approved) {
    setText(doc, { size: TYPE.title, style: 'bold' });
    chip(doc, 'PROPOSED', titleX + doc.getTextWidth('AI INSPECTION REPORT') + 4, y + 1, { fill: COLOR.brand, size: TYPE.caption });
  }

  const facts = [
    { label: 'IMAGE', value: model.imageLabel || model.scanName || EM_DASH },
    { label: 'JOB', value: model.jobSub || model.jobLabel },
    { label: 'DATE / TIME', value: model.dateLabel },
    { label: 'MODEL', value: model.aiModel || NOT_RECORDED },
    { label: 'INSPECTOR REVIEW', value: model.reviewStatus, color: model.approved ? COLOR.brand : COLOR.pending },
  ];
  const factWidth = 40;
  facts.forEach((fact, i) => {
    const x = PAGE.width - PAGE.margin - factWidth * (facts.length - i);
    text(doc, fact.label, x, y, { size: TYPE.metaLabel, style: 'bold', color: COLOR.soft });
    text(doc, asciify(fact.value), x, y + 3.2, { size: TYPE.metaValue - 0.5, style: 'bold', color: fact.color || COLOR.ink });
  });

  const bottom = y + 12;
  rule(doc, PAGE.margin, bottom, CONTENT_WIDTH);
  return bottom + 4;
}

/* ---------------------------------------------------------------------------------------------------------
   Page 1 - left column
   --------------------------------------------------------------------------------------------------------- */
const KPI_HEIGHT = 15;

function drawSummary(doc, kpis, y) {
  const cursor = sectionHead(doc, 'Summary', PAGE.margin, y);
  const cells = [
    { label: 'TOTAL FINDINGS', value: String(kpis.total) },
    { label: 'CORROSION FINDINGS', value: String(kpis.corrosion), color: COLOR.alert },
    { label: 'COATING BREAKDOWN', value: String(kpis.coatingBreakdown) },
    { label: 'POTENTIAL SCAB', value: String(kpis.scabCandidates), color: COLOR.alert },
    { label: 'AVG CONFIDENCE', value: formatPercent(kpis.averageConfidence) },
    { label: 'REL. VISIBLE AREA', value: formatPercent(kpis.areaMetric, AREA_DECIMALS) },
  ];
  const gap = 2;
  const width = (LEFT_WIDTH - gap * (cells.length - 1)) / cells.length;
  cells.forEach((cell, i) => {
    const x = PAGE.margin + i * (width + gap);
    box(doc, x, cursor, width, KPI_HEIGHT, { fill: COLOR.white });
    text(doc, cell.label, x + width / 2, cursor + 2.4, { size: TYPE.kpiLabel - 0.5, style: 'bold', color: COLOR.soft, align: 'center' });
    text(doc, cell.value, x + width / 2, cursor + 6.2, { size: TYPE.kpiValue - 1, style: 'bold', color: cell.color || COLOR.brand, align: 'center' });
  });
  return cursor + KPI_HEIGHT + 4;
}

function drawTableHead(doc, columns, x, width, y) {
  doc.setFillColor(...COLOR.brand);
  doc.rect(x, y, width, TABLE_HEAD_HEIGHT, 'F');
  columns.forEach((column) => {
    text(doc, column.label, cellX(column), y + 1.3, { size: TYPE.tableHead, style: 'bold', color: COLOR.white, align: column.align });
  });
  return y + TABLE_HEAD_HEIGHT;
}

function drawTableRow(doc, columns, x, width, row, y, striped) {
  if (striped) {
    doc.setFillColor(...COLOR.surface);
    doc.rect(x, y, width, TABLE_ROW_HEIGHT, 'F');
  }
  columns.forEach((column) => {
    const value = row[column.key];
    const isScab = column.key === 'scab';
    setText(doc, {
      size: TYPE.tableBody,
      style: column.key === 'id' || isScab ? 'bold' : 'normal',
      color: isScab ? SCAB_COLOR[value] || COLOR.body : column.key === 'id' ? COLOR.ink : COLOR.body,
    });
    const clipped = doc.splitTextToSize(String(value), column.width - CELL_PAD * 2)[0] || '';
    doc.text(clipped, cellX(column), y + 1.2, { align: column.align, baseline: 'top' });
  });
  rule(doc, x, y + TABLE_ROW_HEIGHT, width, [238, 241, 243]);
  return y + TABLE_ROW_HEIGHT;
}

/**
 * The table on page 1 stops above the panels; whatever is left continues full-width on page 2. Returns the
 * index of the first row that did not fit.
 */
function drawPage1Table(doc, model, y) {
  let cursor = sectionHead(doc, 'Detected findings', PAGE.margin, y);
  const columns = columnGeometry(PAGE.margin, LEFT_WIDTH);
  cursor = drawTableHead(doc, columns, PAGE.margin, LEFT_WIDTH, cursor);
  const limit = PANEL_TOP - 4;
  let index = 0;
  while (index < model.findings.length && cursor + TABLE_ROW_HEIGHT <= limit) {
    const finding = model.findings[index];
    cursor = drawTableRow(doc, columns, PAGE.margin, LEFT_WIDTH, rowFor(finding, model.sizeById[finding.imageId] || {}), cursor, index % 2 === 1);
    index += 1;
  }
  return { firstUnfit: index, bottom: cursor };
}

function drawOverflowTable(doc, model, fromIndex) {
  doc.addPage();
  let cursor = sectionHead(doc, `Detected findings (continued, ${fromIndex + 1}-${model.findings.length})`, PAGE.margin, PAGE.margin);
  const columns = columnGeometry(PAGE.margin, CONTENT_WIDTH);
  cursor = drawTableHead(doc, columns, PAGE.margin, CONTENT_WIDTH, cursor);
  for (let index = fromIndex; index < model.findings.length; index += 1) {
    if (cursor + TABLE_ROW_HEIGHT > CONTENT_BOTTOM) {
      doc.addPage();
      cursor = drawTableHead(doc, columns, PAGE.margin, CONTENT_WIDTH, PAGE.margin);
    }
    const finding = model.findings[index];
    cursor = drawTableRow(doc, columns, PAGE.margin, CONTENT_WIDTH, rowFor(finding, model.sizeById[finding.imageId] || {}), cursor, index % 2 === 1);
  }
}

/** The two panels sit directly under the table when it ends early, on the page foot when it does not. */
function drawPanels(doc, model, tableBottom) {
  const gap = 4;
  const width = (LEFT_WIDTH - gap) / 2;
  const pad = 3;
  const top = Math.min(PANEL_TOP, tableBottom + 6);

  // Inspection basis
  let x = PAGE.margin;
  box(doc, x, top, width, PANEL_HEIGHT, { fill: COLOR.surface });
  let cursor = sectionHead(doc, 'Inspection basis', x + pad, top + pad);
  INSPECTION_BASIS.forEach((row) => {
    text(doc, asciify(row.label), x + pad, cursor, { size: TYPE.caption - 0.5, style: 'bold', color: COLOR.soft });
    cursor = paragraph(doc, asciify(row.value), x + pad + 26, cursor, width - pad * 2 - 26, { size: TYPE.caption - 0.5, color: COLOR.body, maxLines: 2 }) + 0.4;
  });

  // Scab / Validation
  x = PAGE.margin + width + gap;
  box(doc, x, top, width, PANEL_HEIGHT, { fill: COLOR.surface });
  cursor = sectionHead(doc, 'Scab / Validation', x + pad, top + pad);
  // Chips are taller than a text line, so the rows step by the chip, not the label, and the label is centred on it.
  const chipSize = TYPE.caption - 1;
  const chipH = lineHeight(chipSize) + 1;
  const chipCol = 17;
  SCAB_LEGEND.forEach((entry) => {
    chip(doc, entry.value, x + pad, cursor, { fill: SCAB_COLOR[entry.value], size: chipSize });
    const labelY = cursor + (chipH - lineHeight(TYPE.caption - 0.5)) / 2;
    paragraph(doc, asciify(entry.label), x + pad + chipCol, labelY, width - pad * 2 - chipCol, { size: TYPE.caption - 0.5, color: COLOR.body, maxLines: 1 });
    cursor += chipH + 1;
  });
  const decided = `${model.decidedCount} of ${model.totalCount} findings decided`;
  const state = model.approved ? `Validation: reviewed. ${decided}.` : `Validation: proposed - inspector review required. ${decided}.`;
  paragraph(doc, state, x + pad, cursor + 0.6, width - pad * 2, { size: TYPE.caption - 0.5, style: 'bold', color: model.approved ? COLOR.brand : COLOR.pending });
}

/* ---------------------------------------------------------------------------------------------------------
   Page 1 - right column
   --------------------------------------------------------------------------------------------------------- */

function drawAnnotatedImage(doc, model, y) {
  const cursor = sectionHead(doc, `Annotated original image - ${model.scanName}`, RIGHT_X, y);
  const frame = 3;
  const inner = RIGHT_WIDTH - frame * 2;

  if (!model.annotated) {
    box(doc, RIGHT_X, cursor, RIGHT_WIDTH, 24, { fill: COLOR.surface });
    paragraph(doc, asciify(model.annotatedError || 'No scan image available.'), RIGHT_X + frame, cursor + 8, inner, { size: TYPE.body, color: COLOR.soft });
    return cursor + 28;
  }

  const height = inner * (model.annotated.height / model.annotated.width);
  box(doc, RIGHT_X, cursor, RIGHT_WIDTH, height + frame * 2, {});
  doc.addImage(model.annotated.dataUrl, 'JPEG', RIGHT_X + frame, cursor + frame, inner, height, undefined, 'FAST');
  return cursor + height + frame * 2 + 5;
}

function drawValidationPanel(doc, model, y) {
  const pad = 3;
  const inner = RIGHT_WIDTH - pad * 2;
  const bodyH = paragraphHeight(doc, asciify(PDF_CONTOUR_LIMITATION), inner, { size: TYPE.caption });
  const height = pad + lineHeight(TYPE.sectionHead) + bodyH + lineHeight(TYPE.body) + lineHeight(TYPE.caption) + pad + 4;

  box(doc, RIGHT_X, y, RIGHT_WIDTH, height, { fill: COLOR.surface });
  let cursor = sectionHead(doc, 'Validation status / contour limitation', RIGHT_X + pad, y + pad);
  cursor = paragraph(doc, asciify(PDF_CONTOUR_LIMITATION), RIGHT_X + pad, cursor, inner, { size: TYPE.caption, color: COLOR.body });
  const headline = model.approved ? 'REVIEWED - EVERY FINDING CARRIES AN INSPECTOR DECISION' : 'PROPOSED - INSPECTOR REVIEW REQUIRED';
  cursor = text(doc, headline, RIGHT_X + pad, cursor + 2, { size: TYPE.body, style: 'bold', color: model.approved ? COLOR.brand : COLOR.alert });
  text(doc, asciify(PDF_NO_INFERENCE), RIGHT_X + pad, cursor, { size: TYPE.caption, color: COLOR.soft });
  return y + height;
}

/* ---------------------------------------------------------------------------------------------------------
   Detailed findings - two columns of cards
   --------------------------------------------------------------------------------------------------------- */
const CARD_GAP = 5;
const CARD_PAD = 2.6;
const BADGE_R = 2.2;
/** Room reserved for the scab chip + its confidence at the start of the description line. */
const SCAB_INSET = 17;
/** Text column of a card: right of the badge, with a small gutter. */
const CARD_INDENT = BADGE_R * 2 + 2.2;

function cardCopy(finding, size) {
  const scab = String(finding.scabMeasure || '').toUpperCase();
  return {
    title: asciify(finding.locationDescription || EM_DASH),
    metrics: asciify(
      [
        finding.masterTagName ? `Component ${finding.masterTagName}` : '',
        `Ri ${stripRi(finding.coatingBreakdown) || '-'}`,
        `Cat ${stripCat(finding.pittingCategory) || '-'}`,
        `Corrosion ${percentOf(finding.detectionConfidence)}%`,
        `Polygon ${percentOf(finding.polygonConfidence)}%`,
        `Rel. area ${areaPercent(finding, size)} image`,
        `Review: ${REVIEW_LABEL[finding.approvalStatus] || 'Pending'}`,
      ]
        .filter(Boolean)
        .join(' | '),
    ),
    scab,
    scabConf: scab ? `${percentOf(finding.scabConfidence)}%` : '',
    description: asciify(finding.description || EM_DASH),
    reason: finding.scabReason ? asciify(`Scab reason: ${finding.scabReason}`) : '',
  };
}

function cardHeight(doc, copy, width) {
  const bodyW = width - CARD_PAD * 2 - CARD_INDENT;
  let h = CARD_PAD;
  h += paragraphHeight(doc, copy.title, bodyW, { size: TYPE.cardTitle, maxLines: 2 }) + 0.8;
  h += lineHeight(TYPE.caption) + 1;
  h += paragraphHeight(doc, copy.description, bodyW - (copy.scab ? SCAB_INSET : 0), { size: TYPE.caption, maxLines: 4 });
  if (copy.reason) h += paragraphHeight(doc, copy.reason, bodyW, { size: TYPE.caption, maxLines: 3 }) + 1;
  return h + CARD_PAD;
}

function drawCard(doc, finding, copy, x, y, width, height) {
  const inner = width - CARD_PAD * 2;
  box(doc, x, y, width, height, { fill: COLOR.white });

  doc.setFillColor(...COLOR.badge);
  doc.circle(x + CARD_PAD + BADGE_R, y + CARD_PAD + BADGE_R, BADGE_R, 'F');
  setText(doc, { size: TYPE.caption - 0.5, style: 'bold', color: COLOR.white });
  doc.text(String(finding.badgeIndex), x + CARD_PAD + BADGE_R, y + CARD_PAD + BADGE_R, { align: 'center', baseline: 'middle' });

  const textX = x + CARD_PAD + CARD_INDENT;
  const bodyW = inner - CARD_INDENT;
  let cursor = paragraph(doc, copy.title, textX, y + CARD_PAD - 0.3, bodyW, { size: TYPE.cardTitle, style: 'bold', color: COLOR.ink, maxLines: 2 });
  cursor = text(doc, copy.metrics, textX, cursor + 0.8, { size: TYPE.caption, color: COLOR.soft }) + 1;

  let bodyX = textX;
  if (copy.scab) {
    const w = chip(doc, copy.scab, bodyX, cursor - 0.4, { fill: SCAB_COLOR[copy.scab] || COLOR.soft, size: TYPE.caption - 1 });
    text(doc, copy.scabConf, bodyX + w + 1.5, cursor, { size: TYPE.caption, style: 'bold', color: COLOR.ink });
    bodyX += SCAB_INSET;
  }
  cursor = paragraph(doc, copy.description, bodyX, cursor, bodyW - (bodyX - textX), { size: TYPE.caption, color: COLOR.body, maxLines: 4 });
  if (copy.reason) paragraph(doc, copy.reason, textX, cursor + 1, bodyW, { size: TYPE.caption, color: COLOR.soft, maxLines: 3 });
}

function drawDetailHeader(doc, model) {
  const y = PAGE.margin;
  const size = TYPE.wordmark - 4;
  setText(doc, { size, style: 'bold', color: COLOR.brand });
  doc.text('AVA', PAGE.margin, y + 4.5, { baseline: 'middle' });
  let x = PAGE.margin + doc.getTextWidth('AVA') + 2;
  const title = `| ${asciify(PDF_DETAIL_TITLE)}`;
  setText(doc, { size, style: 'bold', color: COLOR.ink });
  doc.text(title, x, y + 4.5, { baseline: 'middle' });
  x += doc.getTextWidth(title) + 4;
  if (!model.approved) x += chip(doc, 'PROPOSED', x, y + 1.8, { fill: COLOR.brand, size: TYPE.caption }) + 4;
  text(doc, asciify(`${REPORT_SCOPE} | ${REPORT_MODE} | ${model.scanName}`), x, y + 2.4, { size: TYPE.caption, color: COLOR.soft });
  const bottom = y + 10;
  rule(doc, PAGE.margin, bottom, CONTENT_WIDTH);
  return bottom + 5;
}

function drawDetailPages(doc, model) {
  doc.addPage();
  let top = drawDetailHeader(doc, model);
  const width = (CONTENT_WIDTH - CARD_GAP) / 2;
  let columnY = [top, top];

  model.findings.forEach((finding) => {
    const copy = cardCopy(finding, model.sizeById[finding.imageId] || {});
    const height = cardHeight(doc, copy, width);
    let side = columnY[0] <= columnY[1] ? 0 : 1;
    if (columnY[side] + height > CONTENT_BOTTOM) {
      const other = side === 0 ? 1 : 0;
      if (columnY[other] + height <= CONTENT_BOTTOM) {
        side = other;
      } else {
        doc.addPage();
        top = drawDetailHeader(doc, model);
        columnY = [top, top];
        side = 0;
      }
    }
    drawCard(doc, finding, copy, PAGE.margin + side * (width + CARD_GAP), columnY[side], width, height);
    columnY[side] += height + CARD_GAP;
  });
}

/* ---------------------------------------------------------------------------------------------------------
   Document
   --------------------------------------------------------------------------------------------------------- */

function fileNameFor(model) {
  const safe = String(model.jobLabel || 'report').replace(/[^A-Za-z0-9_-]+/g, '_');
  return `AVA_AI_Analysis_Report_${safe}.pdf`;
}

export function buildReportPdf(model) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
  const kpis = computeKpis(model.findings, model.sizeById);

  /* page 1 */
  const top = drawMasthead(doc, model);
  const afterSummary = drawSummary(doc, kpis, top);
  const { firstUnfit, bottom } = drawPage1Table(doc, model, afterSummary);
  drawPanels(doc, model, bottom);
  const afterImage = drawAnnotatedImage(doc, model, top);
  drawValidationPanel(doc, model, afterImage);

  /* overflow table, only if page 1 could not hold every row */
  if (firstUnfit < model.findings.length) drawOverflowTable(doc, model, firstUnfit);

  /* detailed findings */
  if (model.findings.length) drawDetailPages(doc, model);

  const pageCount = doc.internal.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    drawFooter(doc, model, page, pageCount);
  }
  return doc;
}

/**
 * Renders the annotated scan (needs blob-storage CORS), builds the document and saves it. A failed image is
 * not fatal: the report is still worth having, and page 1 says why the picture is missing.
 *
 * @returns {Promise<{ fileName: string, imageIncluded: boolean, imageError: string|null }>}
 */
export async function generateReportPdf(model) {
  let annotated = null;
  let annotatedError = null;

  if (model.imageUrl) {
    try {
      // Only the pictured scan's contours: `findings` spans every scan of the job.
      annotated = await renderAnnotatedImage(model.imageUrl, model.imageFindings || model.findings);
    } catch (err) {
      logError('[AI Analysis] annotated image could not be rendered for the PDF', err);
      annotatedError = 'The annotated image could not be included because the scan image could not be loaded.';
    }
  } else {
    annotatedError = 'No scan image available for this job.';
  }

  const doc = buildReportPdf({ ...model, annotated, annotatedError });
  const fileName = fileNameFor(model);
  doc.save(fileName);
  return { fileName, imageIncluded: !!annotated, imageError: annotatedError };
}

export default generateReportPdf;
